// Client-safe billing plan catalog.
// Shared between marketing, signup, settings/billing, and the Stripe webhook
// (the webhook imports the same source of truth to map price_id → plan).

export type PlanKey = "starter" | "pro" | "super";
export type BillingInterval = "monthly" | "annual";

export type PlanLimits = {
  workspace_limit: number;
  team_member_limit: number;
  // null = unlimited
  import_history_retention_days: number | null;
};

export type PlanDef = {
  key: PlanKey;
  // Maps to the `account_plan` enum in Postgres
  accountPlan: "starter_affiliate" | "pro_affiliate" | "super_affiliate";
  name: string;
  tagline: string;
  popular?: boolean;
  priceIds: Record<BillingInterval, string>;
  display: Record<BillingInterval, { amount: number; suffix: string }>;
  limits: PlanLimits;
  features: string[];
};

export const TRIAL_DAYS = 14;

export const PLANS: Record<PlanKey, PlanDef> = {
  starter: {
    key: "starter",
    accountPlan: "starter_affiliate",
    name: "Starter Affiliate",
    tagline: "For affiliates running a focused stack",
    priceIds: {
      monthly: "price_1TXE8M8JkRvPH23YYTW7HMDO",
      annual: "price_1TXEAa8JkRvPH23YaOPMT79j",
    },
    display: {
      monthly: { amount: 47, suffix: "/mo" },
      annual: { amount: 397, suffix: "/yr" },
    },
    limits: {
      workspace_limit: 2,
      team_member_limit: 1,
      import_history_retention_days: 30,
    },
    features: [
      "2 affiliate workspaces",
      "1 user (no teammates)",
      "AI-powered CSV import & column mapping",
      "All segment exports",
      "Anomaly detection per workspace",
      "Email digest notifications",
      "30 days of import history",
    ],
  },
  pro: {
    key: "pro",
    accountPlan: "pro_affiliate",
    name: "Pro Affiliate",
    tagline: "For affiliates running a real portfolio",
    popular: true,
    priceIds: {
      monthly: "price_1TXE8r8JkRvPH23YYtTZ7FAM",
      annual: "price_1TXEAE8JkRvPH23YBsWlfnol",
    },
    display: {
      monthly: { amount: 97, suffix: "/mo" },
      annual: { amount: 897, suffix: "/yr" },
    },
    limits: {
      workspace_limit: 5,
      team_member_limit: 2, // 1 main + 1 teammate
      import_history_retention_days: 365,
    },
    features: [
      "Everything in Starter, plus:",
      "5 affiliate workspaces",
      "1 user + 1 team member with permissions",
      "Cross-program People view with LTV intelligence",
      "Custom Lenses for filtered views",
      "Webhook push to CRM",
      "Affiliate Training Library access",
      "Slack notifications",
      "1 year of import history",
      "Metric Sanity Checker enabled",
    ],
  },
  super: {
    key: "super",
    accountPlan: "super_affiliate",
    name: "Super Affiliate",
    tagline: "For affiliates running an empire",
    priceIds: {
      monthly: "price_1TXE998JkRvPH23YP993eDL2",
      annual: "price_1TXE9V8JkRvPH23Yle1ykk6s",
    },
    display: {
      monthly: { amount: 197, suffix: "/mo" },
      annual: { amount: 1497, suffix: "/yr" },
    },
    limits: {
      workspace_limit: 15,
      team_member_limit: 4, // 1 main + 3 teammates
      import_history_retention_days: null,
    },
    features: [
      "Everything in Pro, plus:",
      "15 affiliate workspaces",
      "1 main + 3 team members with permissions",
      "AI Bonus Builder for new launches",
      "Launch Calendar per program",
      "Swipe Files storage",
      "Cross-program reconciliation reports",
      "API access",
      "Early access to v2 features (automatic dashboard sync)",
      "Unlimited import history",
    ],
  },
};

export const PLAN_ORDER: PlanKey[] = ["starter", "pro", "super"];

// Reverse map: price_id → { plan, interval }
const PRICE_INDEX: Record<string, { plan: PlanKey; interval: BillingInterval }> = (() => {
  const out: Record<string, { plan: PlanKey; interval: BillingInterval }> = {};
  for (const plan of PLAN_ORDER) {
    const def = PLANS[plan];
    out[def.priceIds.monthly] = { plan, interval: "monthly" };
    out[def.priceIds.annual] = { plan, interval: "annual" };
  }
  return out;
})();

export function lookupPriceId(
  priceId: string | null | undefined,
): { plan: PlanKey; interval: BillingInterval } | null {
  if (!priceId) return null;
  return PRICE_INDEX[priceId] ?? null;
}

export function planLimits(plan: PlanKey): PlanLimits {
  return PLANS[plan].limits;
}

// Maps the `account_plan` enum value back to our PlanKey (or null for trial/comped/paused)
export function planKeyFromAccountPlan(
  accountPlan: string,
): PlanKey | null {
  switch (accountPlan) {
    case "starter_affiliate":
      return "starter";
    case "pro_affiliate":
      return "pro";
    case "super_affiliate":
      return "super";
    default:
      return null;
  }
}
